"use client";

import { FileText, Loader2, User } from "lucide-react";
import {
  type TranscriptMessage,
  EMOTION_MAP,
  parseTranscript,
} from "@/lib/transcript";

const PERSON_COLORS: Record<
  string,
  { bg: string; border: string; avatar: string; name: string }
> = {
  Host: {
    bg: "bg-blue-500/10 dark:bg-blue-500/15",
    border: "border-blue-500/20",
    avatar: "bg-blue-500 text-white",
    name: "Host",
  },
  Guest: {
    bg: "bg-emerald-500/10 dark:bg-emerald-500/15",
    border: "border-emerald-500/20",
    avatar: "bg-emerald-500 text-white",
    name: "Guest",
  },
};

function getPersonConfig(person: string) {
  return (
    PERSON_COLORS[person] ?? {
      bg: "bg-muted/50",
      border: "border-border",
      avatar: "bg-muted-foreground text-background",
      name: person,
    }
  );
}

function TranscriptBubble({ message }: { message: TranscriptMessage }) {
  const config = getPersonConfig(message.person);
  const isHost = message.person === "Host";
  const emotion = EMOTION_MAP[message.emotion];

  return (
    <div className={`flex w-full gap-3 ${isHost ? "flex-row" : "flex-row-reverse"}`}>
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-xs font-bold ${config.avatar}`}
      >
        <User className="w-4 h-4" />
      </div>
      <div
        className={`flex-1 min-w-0 max-w-[92%] rounded-2xl border px-3 py-3 sm:max-w-[80%] sm:px-4 ${config.bg} ${config.border} ${
          isHost ? "rounded-tl-sm" : "rounded-tr-sm"
        }`}
      >
        <p className="text-xs font-semibold mb-1 opacity-70">{config.name}</p>
        <p className="text-sm leading-relaxed text-foreground/90">{message.text}</p>
        {message.emotion !== "none" && (
          <div className="flex items-center gap-1 mt-1.5">
            <span className="text-base">{emotion.emoji}</span>
            <span className="text-[10px] text-muted-foreground italic">
              {emotion.label}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

interface TranscriptViewerProps {
  transcript: string | null;
  loadingTranscript: boolean;
}

export function TranscriptViewer({
  transcript,
  loadingTranscript,
}: TranscriptViewerProps) {
  if (loadingTranscript) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading transcript…
      </div>
    );
  }

  if (!transcript) {
    return (
      <p className="text-sm text-muted-foreground">
        Transcript not available.
      </p>
    );
  }

  let messages: TranscriptMessage[] = [];
  let parseError: string | null = null;
  try {
    messages = parseTranscript(transcript);
  } catch (e) {
    parseError = e instanceof Error ? e.message : "Invalid transcript format";
  }

  if (parseError) {
    return (
      <div className="rounded-xl border border-red-500/20 bg-card p-6 space-y-2">
        <p className="text-sm font-medium text-red-500">
          Failed to parse transcript: {parseError}
        </p>
        <pre className="text-sm whitespace-pre-wrap font-mono leading-relaxed text-foreground/90 bg-muted/50 rounded-lg p-4">
          {transcript}
        </pre>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-col gap-4 rounded-xl border bg-card p-4 sm:p-6">
      <div className="flex items-center gap-2 text-sm font-medium">
        <FileText className="w-4 h-4 text-blue-500" />
        Transcript
      </div>

      {messages.length > 0 ? (
        <div className="space-y-4 pr-2 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
          {messages.map((msg, i) => (
            <TranscriptBubble key={i} message={msg} />
          ))}
        </div>
      ) : (
        <div className="rounded-lg bg-muted/50 p-4">
          <pre className="text-sm whitespace-pre-wrap font-mono leading-relaxed text-foreground/90">
            {transcript}
          </pre>
        </div>
      )}
    </div>
  );
}
